import apiClient from '../apiClient';
import type { ApiResponse, DashboardStats, RevenueDataPoint, Transaction, RevenueByMachine } from '@/types';
import { getUrlWithQueryParams } from '@/lib/utils';
import { useQuery } from '@tanstack/react-query';
import { ReactQueryKeys } from '../reactQueryKeys';

const BASE_URL = '/dashboard';

export const getStats = async (): Promise<DashboardStats> => {
  const { data } = await apiClient.get<ApiResponse<DashboardStats>>(`${BASE_URL}/stats`);
  return data.data;
};

export const getRevenueChart = async (
  period: 'daily' | 'weekly' | 'monthly' = 'daily',
  dateFrom?: string,
  dateTo?: string
): Promise<RevenueDataPoint[]> => {
  const url = getUrlWithQueryParams(`/analytics/revenue`, {
    period,
    dateFrom,
    dateTo,
  });
  const { data } = await apiClient.get<ApiResponse<RevenueDataPoint[]>>(url);
  return data.data;
};

export const getRecentTransactions = async (limit: number = 10): Promise<Transaction[]> => {
  const url = getUrlWithQueryParams(`${BASE_URL}/transactions`, {
    page: 1,
    limit,
    sortBy: 'createdAt',
    sortOrder: 'desc',
  });
  const { data } = await apiClient.get<ApiResponse<Transaction[]>>(url);
  return data.data;
};

export const getTopMachines = async (): Promise<RevenueByMachine[]> => {
  const { data } = await apiClient.get<ApiResponse<RevenueByMachine[]>>(`/analytics/revenue/by-machine`);
  return data.data;
};

// React Query Hooks

export const useGetDashboardStats = () =>
  useQuery({
    queryKey: [ReactQueryKeys.DASHBOARD_STATS],
    queryFn: () => getStats(),
    refetchInterval: 30000,
  });

export const useGetRecentTransactions = (limit: number = 10) =>
  useQuery({
    queryKey: [ReactQueryKeys.RECENT_TRANSACTIONS, limit],
    queryFn: () => getRecentTransactions(limit),
    refetchInterval: 30000,
  });

export const useGetTopMachines = () =>
  useQuery({
    queryKey: [ReactQueryKeys.ANALYTICS_REVENUE_BY_MACHINE],
    queryFn: () => getTopMachines(),
  });

export const useGetRevenueChart = (
  period: 'daily' | 'weekly' | 'monthly' = 'daily',
  dateFrom?: string,
  dateTo?: string
) =>
  useQuery({
    queryKey: [ReactQueryKeys.DASHBOARD_REVENUE, period, dateFrom, dateTo],
    queryFn: () => getRevenueChart(period, dateFrom, dateTo),
  });
